import React, { useState, useEffect, useRef, useCallback } from "react";
import './AboutUs.css';
import studioLogo from '../assets/LOGO.png';
import consultingBanner from '../assets/consulting-banner.png';
import designDevBg from '../assets/design-dev-bg.png';
import fabricationBg from '../assets/fabrication-bg.png';
import { projectMedia } from '../utils/sharedMedia';

const SCREAM_TEAM_PHOTOS = [
  'WhatsApp Image 2026-07-219 at 3.34.26 PM.jpeg',
  'WhatsApp Image 2026-07-29 1at 3.34.24 PM.jpeg',
  'WhatsApp Image 2026-07-29 at 3.34.19 PM.jpeg',
  'WhatsApp Image 2026-07-29 at 3.34.23 PM.jpeg',
  'WhatsApp Image 2026-07-29 at 3.34.23 PM1.jpeg',
  'WhatsApp Image 2026-07-29 at 3.34.24 P1M.jpeg',
  'WhatsApp Image 2026-07-29 at 3.34.24 PM.jpeg',
  'WhatsApp Image 2026-07-29 at 3.341.25 PM.jpeg',
  'WhatsApp Image 2026-07-29 at 3.341.26 PM.jpeg',
  'WhatsApp Image 2026-07-291 at 3.34.26 PM.jpeg',
  'WhatsApp Image 2026-071-29 at 3.34.25 PM.jpeg',
  'WhatsApp Image 20261-07-29 at 3.34.27 PM.jpeg',
].map((file) => projectMedia('Team', file));

const AboutUs = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());
  const [activePanel, setActivePanel] = useState(0);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const cardRef = useRef();

  const observerRef = useRef();

  const closeLightbox = useCallback(() => setLightboxSrc(null), []);

  const handleScroll = useCallback(() => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setActivePanel(rect.top < 0 ? 1 : 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!lightboxSrc) return undefined;

    function onKeyDown(e) {
      if (e.key === 'Escape') closeLightbox();
    }

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [lightboxSrc, closeLightbox]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach(el => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="about-us-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className={`hero-logo-mark animate-on-scroll ${visibleElements.has('hero-logo') ? 'animate-fade-in-down' : ''}`} id="hero-logo">
            <img src={studioLogo} alt="Studio 13" />
          </div>
          <h1 className={`animate-on-scroll ${visibleElements.has('hero-title') ? 'animate-fade-in-down' : ''}`} id="hero-title">About Studio 13</h1>
          <p className={`hero-subtitle animate-on-scroll ${visibleElements.has('hero-subtitle') ? 'animate-fade-in-up' : ''}`} id="hero-subtitle">Crafting Immersive Horror Experiences Through Innovation & Creativity</p>
          <div className={`hero-stats animate-on-scroll ${visibleElements.has('hero-stats') ? 'animate-fade-in-up' : ''}`} id="hero-stats">
            <div className="stat">
              <span className="stat-number">40+</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat">
              <span className="stat-number">100+</span>
              <span className="stat-label">Projects Completed</span>
            </div>
            <div className="stat">
              <span className="stat-number">250+</span>
              <span className="stat-label">Team Members</span>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase — card stays centered, content crossfades on scroll */}
      <div className="showcase-scroll-zone" ref={cardRef}>
        <div className="container">
          <div className="showcase-card">
            <div className={`showcase-panel ${activePanel === 0 ? 'active' : ''}`}>
              <div className="about-card-inner">
                <div className="about-card-text">
                  <h2 className="about-card-title">About us</h2>
                  <p>
                    Studio 13 is a bold creative division of Luca Miro Imagineering dedicated
                    to crafting unforgettable horror, immersive entertainment, and boundary-pushing
                    experiences that live at the intersection of storytelling, design, and innovation.
                    Founded on decades of international expertise in themed attractions and
                    experiential environments, Studio 13 specializes in transforming fear into art —
                    developing haunted attractions, original IPs, live experiences, and AI-enhanced
                    concepts that captivate audiences and keep them coming back for more. Our work
                    blends cinematic storytelling, cutting-edge technology, and meticulous design
                    to create worlds that feel real, visceral, and emotionally charged. At its core,
                    Studio 13 is driven by a passion for imagination, a love of adrenaline, and a
                    commitment to redefining what modern entertainment can be.
                  </p>
                </div>
                <div className="about-card-logo">
                  <img src={studioLogo} alt="Studio 13 Logo" />
                </div>
              </div>
            </div>
            <div className={`showcase-panel ${activePanel === 1 ? 'active' : ''}`}>
              <div className="consulting-inner">
                <img className="consulting-bg" src={consultingBanner} alt="Studio 13 Consulting" />
                <div className="consulting-overlay">
                  <div className="consulting-content">
                    <h2 className="consulting-title">Consulting</h2>
                    <div className="consulting-columns">
                      <p>
                        Studio 13 Consulting brings together decades of global experience in themed
                        entertainment, attraction development, and immersive design to help clients
                        turn bold ideas into successful, revenue-driven realities. We work closely with
                        developers, investors, and entertainment operators to shape concepts, refine
                        guest experiences, and guide projects from vision to execution.
                      </p>
                      <p>
                        Our expertise spans creative direction, attraction strategy, IP development,
                        and AI integration, ensuring every project is innovative and commercially viable.
                        Whether launching a new venue, enhancing an existing attraction, or building a
                        brand from scratch, Studio 13 Consulting delivers practical insight, creative
                        solutions that elevate entertainment to the next level.
                      </p>
                    </div>
                  </div>
                  <div className="consulting-logo">
                    <img src={studioLogo} alt="Studio 13 Logo" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Leadership Section */}
      <section className="leadership-section">
        <div className="container">
          <div className={`section-header animate-on-scroll ${visibleElements.has('leadership-header') ? 'animate-fade-in-up' : ''}`} id="leadership-header">
            <span className="section-tag">Leadership</span>
            <h2>Meet Our Leadership</h2>
            <div className="section-divider"></div>
          </div>
          
          <div className={`leadership-card animate-on-scroll ${visibleElements.has('leadership-card') ? 'animate-fade-in-up' : ''}`} id="leadership-card">
            <div className="leader-accent"></div>
            <div className="leader-info">
              <h3>LOUIS PECCI “LUCA”</h3>
              <p className="leader-title">CEO & Creative Director</p>
              <p className="leader-description">
                A visionary with 40+ years of crafting immersive worlds for Disney, Universal, and beyond. 
                Luca leads Studio 13 with a burning passion for story, spectacle, and immersive design. 
                Known as "The Architect of Illusions," he brings decades of experience from working with 
                world-renowned entertainment companies.
              </p>
              <div className="leader-achievements">
                <span className="achievement">Disney Veteran</span>
                <span className="achievement">Universal Studios</span>
                <span className="achievement">Planet Hollywood</span>
                <span className="achievement">Ripley's Believe It or Not</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scream Team Section */}
      <section className="scream-team-section">
        <div className="container">
          <div
            className={`section-header animate-on-scroll ${visibleElements.has('scream-team-header') ? 'animate-fade-in-up' : ''}`}
            id="scream-team-header"
          >
            <span className="section-tag">The Crew</span>
            <h2>Meet Our Scream Team</h2>
            <div className="section-divider"></div>
          </div>

          <div
            className={`scream-team-grid animate-on-scroll ${visibleElements.has('scream-team-grid') ? 'animate-fade-in-up' : ''}`}
            id="scream-team-grid"
          >
            {SCREAM_TEAM_PHOTOS.map((src, index) => (
              <button
                type="button"
                className="scream-team-card"
                key={`scream-team-${index}`}
                onClick={() => setLightboxSrc(src)}
                aria-label={`View full photo of scream team member ${index + 1}`}
              >
                <img src={src} alt={`Studio 13 scream team member ${index + 1}`} loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {lightboxSrc && (
        <div
          className="scream-team-lightbox"
          onClick={closeLightbox}
          role="presentation"
        >
          <div
            className="scream-team-lightbox-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Full team photo"
          >
            <button
              type="button"
              className="scream-team-lightbox-close"
              onClick={closeLightbox}
              aria-label="Close full photo"
            >
              &times;
            </button>
            <img src={lightboxSrc} alt="Studio 13 scream team member" className="scream-team-lightbox-image" />
          </div>
        </div>
      )}

      {/* Design & Attraction Development Section */}
      <section className="design-dev-section">
        <div className="container">
          <div className={`design-dev-wrapper animate-on-scroll ${visibleElements.has('design-dev') ? 'animate-fade-in-up' : ''}`} id="design-dev">
            <div className="design-dev-card">
              <img className="design-dev-bg" src={designDevBg} alt="Design & Attraction Development" />
              <div className="design-dev-overlay">
                <div className="design-dev-content">
                  <h2 className="design-dev-title">Design & Attraction Development</h2>
                  <p>
                    At Studio 13, design is more than aesthetics — it is the foundation of every unforgettable
                    experience we create. Our Design & Attraction Development team specializes in transforming
                    concepts into fully realized environments that engage the senses, tell powerful stories,
                    and drive repeat visitation. From haunted attractions and themed venues to interactive
                    installations and immersive worlds, we guide projects through every stage, including
                    concept creation, master planning, layout design, show integration, and guest flow
                    optimization. By blending cinematic storytelling, technical expertise, and innovative
                    technologies, we develop attractions that are visually striking, operationally efficient,
                    and built to leave a lasting emotional impact.
                  </p>
                </div>
              </div>
            </div>
            <div className="design-dev-logo">
              <img src={studioLogo} alt="Studio 13 Logo" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <div className={`section-header animate-on-scroll ${visibleElements.has('services-header') ? 'animate-fade-in-up' : ''}`} id="services-header">
            <span className="section-tag">What We Do</span>
            <h2>Our Expertise</h2>
            <div className="section-divider"></div>
          </div>
          
          <div className={`services-grid animate-on-scroll ${visibleElements.has('services-grid') ? 'animate-fade-in-up' : ''}`} id="services-grid">
            <div className="service-category">
              <span className="service-number">01</span>
              <h3>Creative Development</h3>
              <ul>
                <li>IP Development</li>
                <li>Character & Story Development</li>
                <li>Master Planning</li>
                <li>Design Creative</li>
              </ul>
            </div>
            <div className="service-category">
              <span className="service-number">02</span>
              <h3>Production & Fabrication</h3>
              <ul>
                <li>Fabrication</li>
                <li>Construction</li>
                <li>Costume & Set Design</li>
                <li>Architecture</li>
              </ul>
            </div>
            <div className="service-category">
              <span className="service-number">03</span>
              <h3>Operations</h3>
              <ul>
                <li>Operations</li>
                <li>Event Management</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Fabrication & Build Section */}
      <section className="fabrication-section">
        <div className={`fabrication-card animate-on-scroll ${visibleElements.has('fabrication') ? 'animate-fade-in-up' : ''}`} id="fabrication">
          <div className="fabrication-image">
            <img src={fabricationBg} alt="Fabrication & Build" />
          </div>
          <div className="fabrication-content">
            <h2 className="fabrication-title">Fabrication & Build</h2>
            <p>
              The Fabrication & Build department at Studio 13 brings imagination
              into the physical world, transforming creative concepts into tangible,
              high-impact environments. With a hands-on approach and a deep
              understanding of themed construction, our team oversees the
              production of scenic elements, props, sets, facades, and specialty
              installations with precision and durability in mind. We work closely
              with designers, engineers, and project managers to ensure every
              detail is executed to the highest standards, balancing artistry with
              structural integrity and operational practicality. From initial
              prototyping to final installation, Studio 13 delivers expertly crafted
              builds that make each attraction feel authentic, immersive, and
              built to last.
            </p>
            <div className="fabrication-logo">
              <img src={studioLogo} alt="Studio 13 Logo" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <div className={`section-header animate-on-scroll ${visibleElements.has('values-header') ? 'animate-fade-in-up' : ''}`} id="values-header">
            <span className="section-tag">Our DNA</span>
            <h2>Our Values</h2>
            <div className="section-divider"></div>
          </div>
          
          <div className={`values-grid animate-on-scroll ${visibleElements.has('values-grid') ? 'animate-fade-in-up' : ''}`} id="values-grid">
            <div className="value-card">
              <div className="value-number">01</div>
              <h3>Innovation</h3>
              <p>Pushing creative boundaries and exploring new frontiers in entertainment</p>
            </div>
            <div className="value-card">
              <div className="value-number">02</div>
              <h3>Quality</h3>
              <p>Delivering exceptional results that exceed expectations</p>
            </div>
            <div className="value-card">
              <div className="value-number">03</div>
              <h3>Collaboration</h3>
              <p>Working together to bring visions to life</p>
            </div>
            <div className="value-card">
              <div className="value-number">04</div>
              <h3>Excellence</h3>
              <p>Striving for perfection in every project we undertake</p>
            </div>
          </div>
        </div>
      </section>

      {/* Operations & Brand Development Section */}
      <section className="operations-section">
        <div className={`operations-card animate-on-scroll ${visibleElements.has('operations') ? 'animate-fade-in-up' : ''}`} id="operations">
          <h2 className="operations-title">Operations & Brand Development</h2>
          <p>
            Studio 13's Operations & Brand Development division ensures
            that every experience not only opens strong but thrives long
            after launch. We focus on building sustainable, recognizable
            entertainment brands supported by efficient day-to-day
            operations, clear systems, and consistent guest engagement
            strategies. Our team works across staff training, operational
            planning, guest flow management, revenue optimization,
            and brand positioning to create attractions that run smoothly
            while maintaining a powerful identity in the market. By
            aligning creative vision with practical execution, we help
            transform venues into living brands — experiences that evolve,
            grow loyal followings, and deliver long-term value for both
            audiences and stakeholders.
          </p>
          <div className="operations-logo">
            <img src={studioLogo} alt="Studio 13 Logo" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
